/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo, useState } from 'react';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useGetFormConfigQuery } from '@/features/config/configApi';
import { useDispatch, useSelector } from 'react-redux';
import { changeRowsPerPage } from '@/features/table/clientsTableSlice';
import { useLocation } from 'react-router-dom';
import { paths } from '@/app/routes';
import { InstantSearch, Configure } from 'react-instantsearch';
import { TableRefresh } from '@/components/table/TableRefresh';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { clientSearch } from '@/utils/typesense/clients/clientSearch';
import { adminClientSearch } from '@/utils/typesense/clients/adminClientSearch';
import InstantSearchDataGrid from '../InstantSearchDataGrid';
import { parseHeadCells } from '../InstantSearchDataGrid/data';
import ClientTableFilters from './ClientTableFilters';
import { useInstantSearchOwnerFilters } from '@/hooks/useInstantSearchOwnerFilters';
import { joinFilters } from '@/utils/constants';
import { usePipelineContext } from '@/context/pipelineContext';
import { isProspectFieldVisible, VIEW_TYPES } from '@/features/config/helper';
import { useReferralStatusFields } from '@/hooks/useReferralStatusFields';
import { useAdminModeAccess } from '@/hooks/useAdminModeAccess';
import { clientHeadCells, prospectHeadCells, sharedHeadCells } from './data';

export default function ClientTable({ tab }) {
	const dispatch = useDispatch();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const location = useLocation();
	const { isAdminMode } = useAdminModeAccess();

	const isProspectView = location.pathname.includes(paths.prospects);

	const [prospectFilter, setProspectFilter] = useState('');

	const { rowsPerPage } = useSelector((state) => state.clientTable);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { getFeatureFlags } = useFeatureFlagContext();
	const showProspects = Boolean(getFeatureFlags('prospects')?.active);
	const showClientStatus = Boolean(getFeatureFlags('clientStatusFilter')?.active);
	const useNewClient = Boolean(getFeatureFlags('createNewClient')?.active);
	const showReferralPartner = Boolean(getFeatureFlags('showReferralPartner')?.active);

	const referralFields = useReferralStatusFields();

	const { data: formConfigFields } = useGetFormConfigQuery(
		{ configType: 'client' },
		{ skip: !userDetails || !useNewClient },
	);

	const showReferenceColumn = useMemo(() => {
		if (!useNewClient) return false;
		const refField = (formConfigFields ?? []).find((f) => f.name === 'Ref');
		if (!refField) return false;
		return isProspectView
			? isProspectFieldVisible(refField, VIEW_TYPES.update)
			: Boolean(refField.visible?.[VIEW_TYPES.update]);
	}, [useNewClient, formConfigFields, isProspectView]);

	const { pathname: slug } = useLocation();

	const { filters, ownerFilter } = useInstantSearchOwnerFilters();
	const { active, closed } = usePipelineContext(process.env.REACT_APP_PIPELINE_PROSPECTS);

	const headCells = useMemo(() => {
		const cells = sharedHeadCells.slice();

		if (showProspects) {
			cells.splice(1, 0, { id: 'isProspect', label: 'Client type', sortable: true });
		}

		if (showReferralPartner) {
			cells.push({ id: 'referralPartner', label: 'Referral partner', sortable: true });
		}

		if (referralFields.length > 0) {
			cells.push(
				...referralFields.map((f) => ({ id: f.name, label: f.label, sortable: false })),
			);
		}

		if (showReferenceColumn) {
			cells.push({ id: 'reference', label: 'Reference', sortable: true });
		}

		cells.push(...(isProspectView ? prospectHeadCells : clientHeadCells));
		return cells;
	}, [showProspects, showReferralPartner, referralFields, showReferenceColumn, isProspectView]);

	const defaultHiddenColumns = useMemo(() => {
		const hidden = ['referralPartner'];
		hidden.push(...referralFields.map((f) => f.name));
		return hidden;
	}, [referralFields]);

	const handleChangeRowsPerPage = (event) => {
		dispatch(changeRowsPerPage(event.target.value));
	};

	const filterSchema = useMemo(() => {
		const filterArray = [ownerFilter].filter(Boolean);

		if (isProspectView) {
			const stageIds = (tab === 'closed' ? closed : active) ?? [];
			if (stageIds.length > 0) {
				filterArray.push(`stage:[${stageIds.map((id) => `'${id}'`).join(',')}]`);
			}
			filterArray.push('isProspect:= true');
		}

		if ((filters?.brokerGroupId ?? []).length > 0) {
			filterArray.push(`brokerGroupId: [${filters?.brokerGroupId.join(',')}]`);
		}

		// referralPartner is refined via RefinementListSelect (InstantSearch facets), like stage.

		if (prospectFilter?.length > 0 && !isProspectView) {
			filterArray.push(prospectFilter);
		}

		const isCurrentValue = filters?.isCurrent?.[0];
		if (isCurrentValue && showClientStatus) {
			filterArray.push(isCurrentValue);
		}

		const filterString = joinFilters(filterArray);

		console.log('Client table filters', filterString);
		return {
			filters: filterString,
		};
	}, [
		ownerFilter,
		prospectFilter,
		tab,
		isProspectView,
		filters,
		showClientStatus,
		active,
		closed,
	]);

	const columns = useMemo(() => parseHeadCells(headCells), [headCells]);

	return (
		<InstantSearch
			indexName='clients'
			searchClient={isAdminMode ? adminClientSearch : clientSearch}
		>
			<Configure {...filterSchema} />
			<TableRefresh table='clientTable' />
			<ClientTableFilters
				{...{
					prospectFilter,
					setProspectFilter,
					tab,
				}}
			/>

			<InstantSearchDataGrid
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				objectType={'client'}
				includeTasks={isProspectView}
				columns={columns}
				tab={tab}
				variant={slug}
				defaultHiddenColumns={defaultHiddenColumns}
				enableExport={userDetails?.principleUser}
				enableColumnReorder
			/>
		</InstantSearch>
	);
}
